import { useEffect, useMemo, useState } from "react";
import { Box, Typography, Avatar, Paper, Button } from "@mui/material";
import { Hospital, Pencil } from "lucide-react";
import { DeceasedPerson } from "../../../../../bookings/types/edit-booking";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store/Index";
import ObituaryEditor from "../../../../../../components/common/ObituaryEditor";

interface ObituaryTabProps {
  deceasedData: DeceasedPerson | null;
  streamData?: {
    booking: {
      deceased: Array<{
        id: number;
        person_image?: string;
      }>;
    };
  } | null;
  isLoadingDeceased: boolean;
  isAdmin: boolean;
}

export default function ObituaryTab({
  deceasedData,
  streamData,
  isLoadingDeceased,
  isAdmin,
}: ObituaryTabProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [currentObituary, setCurrentObituary] = useState("");
  const [isEditingObituary, setIsEditingObituary] = useState(false);

  useEffect(() => {
    setCurrentObituary(deceasedData?.orbituary || "");
  }, [deceasedData?.orbituary]);

  const hasObituary = useMemo(() => {
    return Boolean(
      currentObituary && currentObituary.replace(/<[^>]*>/g, "").trim(),
    );
  }, [currentObituary]);

  const shouldShowEditor = isAdmin && (!hasObituary || isEditingObituary);

  if (isLoadingDeceased) {
    return (
      <Typography variant="body1" sx={{ textAlign: "center", mt: 3 }}>
        Loading obituary...
      </Typography>
    );
  }

  if (!deceasedData) {
    return (
      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
          mt: 3,
          color: reduxTheme.grey_colors[500],
        }}
      >
        No obituary data available.
      </Typography>
    );
  }

  return (
    <Paper sx={{ p: 2, boxShadow: 0, borderRadius: "20px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ width: { xs: "100%", md: "33.333%" } }}>
          <Box
            sx={{
              width: { xs: "60%", md: "100%" },
              height: { xs: 200, md: 300 },
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: reduxTheme.primary_colors[900],
              flexDirection: "column",
              mx: { xs: "auto", md: 0 },
              mb: { xs: 3, md: 0 },
              overflow: "hidden",
            }}
          >
            <Avatar
              src={
                deceasedData?.person_image ||
                streamData?.booking.deceased[0]?.person_image ||
                ""
              }
              sx={{
                width: { xs: 150, md: 200 },
                height: { xs: 150, md: 200 },
                mb: 1,
              }}
            />
          </Box>
        </Box>

        <Box sx={{ width: { xs: "100%", md: "66.666%" } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              flexWrap: "wrap",
              mb: 3,
            }}
          >
            <Box
              sx={{
                flexBasis: { xs: "100%", md: "33.333%" },
                textAlign: "center",
              }}
            >
              <Box>
                <Hospital />
              </Box>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "0.625rem", md: "0.75rem" },
                  color: reduxTheme.primary_colors[100],
                }}
              >
                SUNRISE
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{
                  fontSize: { xs: "0.625rem", md: "0.75rem" },
                }}
              >
                {deceasedData?.date_of_birth
                  ? new Date(deceasedData.date_of_birth).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )
                  : "N/A"}
              </Typography>
            </Box>

            <Box
              sx={{
                flexBasis: { xs: "100%", md: "33.333%" },
                textAlign: "center",
              }}
            >
              <Box>
                <Hospital />
              </Box>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "0.625rem", md: "0.75rem" },
                  color: reduxTheme.primary_colors[100],
                }}
              >
                SUNSET
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{
                  fontSize: { xs: "0.625rem", md: "0.75rem" },
                  color: reduxTheme.primary_colors[100],
                }}
              >
                {deceasedData?.date_of_death
                  ? new Date(deceasedData.date_of_death).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )
                  : "N/A"}
              </Typography>
            </Box>
          </Box>

          {shouldShowEditor ? (
            <ObituaryEditor
              obituary={currentObituary}
              deceasedId={deceasedData.id}
              onSaveSuccess={(savedHtml) => {
                setCurrentObituary(savedHtml);
                setIsEditingObituary(false);
              }}
              onCancel={
                hasObituary
                  ? () => {
                      setIsEditingObituary(false);
                    }
                  : undefined
              }
            />
          ) : (
            <>
              {isAdmin && (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}
                >
                  <Button
                    variant="contained"
                    startIcon={<Pencil size={16} />}
                    sx={{
                      backgroundColor: reduxTheme.primary_colors[400],
                    }}
                    onClick={() => setIsEditingObituary(true)}
                  >
                    Edit Obituary
                  </Button>
                </Box>
              )}

              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  backgroundColor: reduxTheme.primary_colors[800],
                  borderRadius: 2,
                  color: reduxTheme.grey_colors[100],
                  "& p": {
                    color: reduxTheme.grey_colors[100],
                    lineHeight: 1.7,
                    fontSize: { xs: "0.875rem", md: "1rem" },
                  },
                  "& h1, & h2, & h3, & h4, & h5, & h6": {
                    color: reduxTheme.grey_colors[100],
                  },
                  "& a": {
                    color: reduxTheme.secondary_colors[300],
                  },
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: currentObituary?.trim()
                      ? currentObituary
                      : "<p>No obituary available.</p>",
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
